import type { AppContext } from '@application/context/AppContext';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { getBusinessApiClient, BusinessApiConflictError } from '@infra/api/BusinessApiClient';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';
import { logger } from '@infra/telemetry/Logger';
import type { Tenant } from '@shared/types/Tenant';
import { assertCanWriteEntity } from '@infra/tenancy/entityWriteGuard';

const RESOURCE = 'tenant-profile';

function stripTenantMeta(tenant: Tenant): Record<string, unknown> {
  const copy = { ...tenant } as Record<string, unknown>;
  delete copy['id'];
  return copy;
}

function stripRemoteCacheMeta(cached: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...cached };
  delete rest['_remoteVersion'];
  delete rest['_dirty'];
  delete rest['id'];
  delete rest['tenant_id'];
  return rest;
}

function mergeTenantRow(tenantId: string, payload: Record<string, unknown>): Tenant {
  return { ...(payload as unknown as Tenant), id: tenantId };
}

/** Profil entreprise tenant-scopé (local Dexie ou business-api). */
export async function getTenantProfile(ctx: AppContext, tenantId: string): Promise<Tenant | null> {
  const mode = ctx.getLockedDataMode?.() ?? null;
  if (!isRemoteMultiStationMode(mode)) {
    return ctx.localTables.get<Tenant>('tenants', tenantId);
  }

  const api = getBusinessApiClient();
  const online = (await api.confirmWriteReady()) && Boolean(api.hasUserSession());

  if (online) {
    try {
      const remote = await api.getTenantProfile();
      if (remote) {
        const tenant = mergeTenantRow(remote.tenant_id, remote.payload);
        if (await RemoteSyncStore.isEnabled()) {
          await RemoteSyncStore.putCache(
            tenantId,
            RESOURCE,
            tenantId,
            stripTenantMeta(tenant),
            remote.version,
            false,
          );
        }
        await ctx.localTables.put('tenants', tenant);
        return tenant;
      }
    } catch (err) {
      logger.warn({ event: 'tenant_profile_remote_fallback', tenantId, error: String(err) });
    }
  }

  const cached = (await RemoteSyncStore.getCache(tenantId, RESOURCE, tenantId)) as
    (Record<string, unknown> & { _remoteVersion?: number; _dirty?: boolean }) | null;
  if (cached) {
    return mergeTenantRow(tenantId, stripRemoteCacheMeta(cached));
  }

  return ctx.localTables.get<Tenant>('tenants', tenantId);
}

export async function saveTenantProfile(ctx: AppContext, tenant: Tenant): Promise<void> {
  assertCanWriteEntity('tenant-profile', 'update');
  const mode = ctx.getLockedDataMode?.() ?? null;
  if (!isRemoteMultiStationMode(mode)) {
    await ctx.localTables.put('tenants', tenant);
    ctx.tenantContext.setTenant(tenant);
    return;
  }

  const payload = stripTenantMeta(tenant);
  const api = getBusinessApiClient();
  const cached = (await RemoteSyncStore.getCache(tenant.id, RESOURCE, tenant.id)) as {
    _remoteVersion?: number;
  } | null;
  const online = (await api.confirmWriteReady()) && Boolean(api.hasUserSession());
  // Navigateur Cloud : pas de cache SQLite, donc aucune version connue. On relit la version courante du serveur
  // avant d'écrire (sinon tout enregistrement après le premier part avec la version 1 et reçoit un conflit).
  let version = cached?._remoteVersion ?? 1;
  if (online && cached?._remoteVersion === undefined) {
    try {
      version = (await api.getTenantProfile())?.version ?? 1;
    } catch {
      version = 1;
    }
  }

  if (online) {
    try {
      const saved = await api.upsertTenantProfile(payload, version);
      if (await RemoteSyncStore.isEnabled()) {
        await RemoteSyncStore.putCache(
          tenant.id,
          RESOURCE,
          tenant.id,
          saved.payload,
          saved.version,
          false,
        );
      }
      await ctx.localTables.put('tenants', mergeTenantRow(saved.tenant_id, saved.payload));
      ctx.tenantContext.setTenant(mergeTenantRow(saved.tenant_id, saved.payload));
      return;
    } catch (err) {
      if (!(err instanceof BusinessApiConflictError)) throw err;
    }
  }

  // Bootstrap / hors ligne : pas de session ou cache sync absent → Dexie/SQLite local
  if (!(await RemoteSyncStore.isEnabled()) || !api.hasUserSession()) {
    await ctx.localTables.put('tenants', tenant);
    ctx.tenantContext.setTenant(tenant);
    return;
  }

  await RemoteSyncStore.putCache(tenant.id, RESOURCE, tenant.id, payload, version, true);
  await RemoteSyncStore.enqueue({
    tenant_id: tenant.id,
    resource: RESOURCE,
    action: 'update',
    entity_id: tenant.id,
    payload,
    base_version: version,
  });
  await ctx.localTables.put('tenants', tenant);
  ctx.tenantContext.setTenant(tenant);
  logger.info({ event: 'tenant_profile_queued_offline', tenantId: tenant.id });
}
